"use client";

import type React from "react";
import { useCallback, useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import ComboSearch from "./ComboSearch";
import { TagsService } from "@/services/TagService";

interface Tag {
  id: string;
  name: string;
  styling?: {
    fontColor: string;
    primaryColor: string;
    secondaryColor: string;
  };
}

interface MultiTagSelectorProps {
  value: string[] | undefined;
  onChange: (selectedIds: string[]) => void;
  className?: string;
  placeholder?: string;
  preloadedTags?: Tag[]; // Tags já carregadas para este cadastro específico
}

const MultiTagSelector: React.FC<MultiTagSelectorProps> = ({
  value = [],
  onChange,
  className = "",
  placeholder = "Selecione uma tag",
  preloadedTags = [],
}) => {
  const [selectedTagId, setSelectedTagId] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const tagsService = new TagsService();

  // Cache para armazenar tags já buscadas
  const tagCacheRef = useRef<Record<string, Tag>>({});

  // Ref para controlar quais tags estão sendo buscadas atualmente
  const fetchingTagsRef = useRef<Set<string>>(new Set());

  // Carregar detalhes das tags selecionadas que não estão nas preloadedTags
  useEffect(() => {
    // Converter preloadedTags para um mapa para acesso mais rápido
    const preloadedTagsMap = new Map(preloadedTags.map((tag) => [tag.id, tag]));

    // Converter selectedTags para um mapa para acesso mais rápido
    const selectedTagsMap = new Map(selectedTags.map((tag) => [tag.id, tag]));

    // Identificar quais IDs precisamos buscar do servidor
    const idsToFetch = value.filter(
      (id) =>
        !preloadedTagsMap.has(id) &&
        !selectedTagsMap.has(id) &&
        !tagCacheRef.current[id] &&
        !fetchingTagsRef.current.has(id)
    );

    if (idsToFetch.length === 0) {
      // Se não há novas tags para buscar, apenas atualize o estado com as tags disponíveis
      const updatedTags = value
        .map(
          (id) =>
            preloadedTagsMap.get(id) ||
            selectedTagsMap.get(id) ||
            tagCacheRef.current[id]
        )
        .filter(Boolean) as Tag[];

      // Só atualiza se realmente houver mudança
      if (
        updatedTags.length !== selectedTags.length ||
        !updatedTags.every((tag) => selectedTagsMap.has(tag.id))
      ) {
        setSelectedTags(updatedTags);
      }
      return;
    }

    // Buscar apenas as tags que ainda não temos
    const fetchMissingTags = async () => {
      // Marcar as tags como sendo buscadas
      idsToFetch.forEach((id) => fetchingTagsRef.current.add(id));

      try {
        // Buscar as tags em paralelo
        const fetchedTagsPromises = idsToFetch.map(async (id) => {
          try {
            const tag = await tagsService.findById(id);
            // Adicionar ao cache
            tagCacheRef.current[id] = tag;
            return tag;
          } catch (error) {
            console.error(`Erro ao buscar tag ${id}:`, error);
            // Adicionar uma tag de erro ao cache para evitar novas tentativas
            const errorTag = {
              id,
              name: `Tag ${id.substring(0, 8)}...`,
              styling: {
                fontColor: "#ffffff",
                primaryColor: "#ff0000",
                secondaryColor: "#cc0000",
              },
            };
            tagCacheRef.current[id] = errorTag;
            return errorTag;
          } finally {
            // Remover da lista de tags sendo buscadas
            fetchingTagsRef.current.delete(id);
          }
        });

        const fetchedTags = await Promise.all(fetchedTagsPromises);

        // Atualizar o estado combinando as tags existentes com as novas
        setSelectedTags((prevTags) => {
          const prevTagsMap = new Map(prevTags.map((tag) => [tag.id, tag]));

          // Adicionar novas tags buscadas
          fetchedTags.forEach((tag) => {
            prevTagsMap.set(tag.id, tag);
          });

          // Filtrar para incluir apenas as tags que estão em value
          return value
            .map(
              (id) =>
                prevTagsMap.get(id) ||
                preloadedTagsMap.get(id) ||
                tagCacheRef.current[id]
            )
            .filter(Boolean) as Tag[];
        });
      } catch (error) {
        console.error("Erro ao buscar tags:", error);
        // Limpar as tags que estavam sendo buscadas
        idsToFetch.forEach((id) => fetchingTagsRef.current.delete(id));
      }
    };

    fetchMissingTags();
  }, [value, preloadedTags, tagsService]);

  const handleAddTag = useCallback(
    (tagId: string) => {
      if (!tagId || value.includes(tagId)) return;

      onChange([...value, tagId]);
      setSelectedTagId(""); // Limpar a seleção após adicionar
    },
    [value, onChange]
  );

  const handleRemoveTag = useCallback(
    (tagId: string) => {
      if (!value.includes(tagId)) return;
      onChange(value?.filter((id) => id !== tagId));
    },
    [value, onChange]
  );

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 max-w-xs">
          <ComboSearch
            id="tag-selector"
            context="tag"
            value={selectedTagId}
            onSelect={handleAddTag}
            placeholder={placeholder}
            className="w-full"
          />
        </div>
      </div>

      {selectedTags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags?.map((tag) => (
            <Badge
              key={tag.id}
              style={{
                backgroundColor: tag.styling?.primaryColor || "#E0E0E0",
                color: tag.styling?.fontColor || "#000000",
                border: `1px solid ${tag.styling?.secondaryColor || "#BDBDBD"}`,
              }}
              className="rounded-md px-3 py-1 flex items-center gap-1"
            >
              {tag.name}
              <Button
                size="icon"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveTag(tag.id);
                }}
                className="h-4 w-4 focus:outline-none"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiTagSelector;
